import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePresence } from '../../context/PresenceContext';
import { StatusDot, StatusSelector } from '../presence/StatusIndicator';
import PresenceSettingsModal from '../presence/PresenceSettingsModal';
import TeamPresenceList from '../presence/TeamPresenceList';
import {
  Home,
  FolderOpen,
  Users,
  Mail,
  Video,
  MessageSquare,
  Briefcase,
  Calendar,
  Shield,
  Activity,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserCircle,
  Receipt,
  DollarSign,
  Wallet,
  Wrench,
  Mic,
  ClipboardList,
  UserPlus,
  Filter,
  Phone,
  PhoneCall,
  Settings,
  FileSignature,
  Sparkles,
  BookOpen,
  Ticket,
  Search,
  Megaphone,
  LayoutList,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout, isAdmin, isStaff, isFirmMember } = useAuth();
  const { myPresence, updateStatus, unreadSummary, emailUnread, UserStatus } = usePresence();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showPresenceSettings, setShowPresenceSettings] = useState(false);
  // Callback-queue badge: actionable-now count, red when any overdue.
  const [cbBadge, setCbBadge] = useState({ count: 0, overdue: 0 });
  React.useEffect(() => {
    if (!(isAdmin || isFirmMember)) return;
    let alive = true;
    const pull = () => {
      import('../../services/api').then(({ default: api }) =>
        api.get('/callbacks/badge').then((r) => { if (alive) setCbBadge(r.data); }).catch(() => {})
      );
    };
    pull();
    const t = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin, isFirmMember]);

  const navItems = [
    { path: '/', icon: Home, label: 'Dashboard', clientVisible: true },
    { path: '/documents', icon: FolderOpen, label: 'Documents', clientVisible: true },
    { path: '/mail', icon: Sparkles, label: 'OuijaMail', staffOnly: true },
    
    
    { path: '/calendar', icon: Calendar, label: 'Calendar', staffOnly: true },
    { path: '/sigil', icon: MessageSquare, label: 'Sigil', clientVisible: true },
    // [Solo] { path: '/visage', icon: Video, label: 'Visage', clientVisible: true },
    { path: '/cases', icon: Briefcase, label: 'Cases', clientVisible: true },
    { path: '/contacts', icon: UserCircle, label: 'Contacts', staffOnly: true },
    { path: '/invoices', icon: Receipt, label: 'Invoices', clientVisible: true },
    { path: '/payment-requests', icon: Receipt, label: 'Payment requests', staffOnly: true },
    { path: '/callbacks', icon: PhoneCall, label: 'Callbacks', staffOnly: true },
    { path: '/triage', icon: Filter, label: 'Triage', staffOnly: true },
    { path: '/intake', icon: UserPlus, label: 'Intake', staffOnly: true },
    { path: '/phone-intake', icon: UserPlus, label: 'Phone Intake', staffOnly: true },
    { path: '/conflicts', icon: Shield, label: 'Conflict Check', staffOnly: true },
    { path: '/signatures', icon: FileSignature, label: 'Signatures', staffOnly: true },
    { path: '/law-library', icon: BookOpen, label: 'Law Library', staffOnly: true },
    { path: '/dictate', icon: Mic, label: 'Dictate', staffOnly: true },
    { path: '/settings', icon: Settings, label: 'Settings', staffOnly: true },
  ];

  const adminItems = [
    { path: '/admin', icon: Shield, label: 'Admin' },
    { path: '/audit', icon: Activity, label: 'Audit Logs' },
    { path: '/portal-activity', icon: Users, label: 'Portal Activity' },
    { path: '/questionnaire-manager', icon: ClipboardList, label: 'Questionnaires' },
    { path: '/portal-announcements', icon: Megaphone, label: 'Announcements' },
    { path: '/onboarding-admin', icon: LayoutList, label: 'Onboarding' },
    // [Solo] { path: '/maintenance', icon: Wrench, label: 'Maintenance' },
  ];

  const filterNavItems = (items) => {
    return items.filter(item => {
      if (isAdmin) return true;
      if (item.adminOnly) return false;
      if (isFirmMember) return true;
      if (item.staffOnly) return false;
      return item.clientVisible || (!item.staffOnly && !item.adminOnly);
    });
  };

  const visibleNavItems = filterNavItems(navItems);

  const isActive = (path) => location.pathname === path;

  const handleNavClick = (e, path) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <div className={`bg-virgo-navy-800 text-white h-screen flex flex-col transition-all duration-300 border-r border-virgo-teal-900 relative z-10 ${collapsed ? 'w-16' : 'w-64'}`}>
      {/* Header with Logo */}
      <div className="p-4 flex items-center justify-between border-b border-virgo-teal-900">
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <img 
              src="/assets/images/virgo_logo.jpg" 
              alt="Virgo" 
              className="h-10 w-10 rounded-lg object-cover ring-2 ring-virgo-gold-400"
            />
            <h1 className="text-xl font-bold text-virgo-gold-400">VIRGO</h1>
          </div>
        )}
        {collapsed && (
          <img 
            src="/assets/images/virgo_logo.jpg" 
            alt="Virgo" 
            className="h-10 w-10 rounded-lg object-cover ring-2 ring-virgo-gold-400 mx-auto"
          />
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-virgo-navy-700 rounded text-virgo-teal-400 hover:text-virgo-teal-300 transition-colors"
        >
          {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            // Show unread badge for Sigil and Mail
            const sigilUnread = item.path === '/sigil' && unreadSummary?.total_unread > 0;
            const mailUnread = item.path === '/mail' && emailUnread?.unread_count > 0;
            const callbacksBadge = item.path === '/callbacks' && cbBadge.count > 0;
            const showUnreadBadge = sigilUnread || mailUnread || callbacksBadge;
            const badgeCount = sigilUnread ? unreadSummary.total_unread : (mailUnread ? emailUnread.unread_count : (callbacksBadge ? cbBadge.count : 0));
            const badgeOverdue = callbacksBadge && cbBadge.overdue > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(e, item.path)}
                className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-virgo-teal-700 to-virgo-teal-600 text-white shadow-lg shadow-virgo-teal-900/50'
                    : 'text-virgo-teal-200 hover:bg-virgo-navy-700 hover:text-virgo-teal-100'
                }`}
                title={collapsed ? item.label : ''}
              >
                <div className="flex items-center space-x-3">
                  <Icon size={20} className="flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </div>
                {showUnreadBadge && (
                  <span className={`${collapsed ? 'absolute right-1 top-1' : ''} ${badgeOverdue ? 'bg-gradient-to-r from-red-600 to-red-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500'} text-white text-xs rounded-full px-1.5 py-0.5 font-medium min-w-[18px] text-center`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <>
            {!collapsed && (
              <div className="px-4 py-2 mt-4 text-xs font-semibold text-virgo-gold-400 uppercase tracking-wider">
                Admin
              </div>
            )}
            <div className="space-y-1 px-2">
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => handleNavClick(e, item.path)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-virgo-gold-600 to-virgo-gold-500 text-virgo-navy-900 shadow-lg shadow-virgo-gold-900/50'
                        : 'text-virgo-gold-300 hover:bg-virgo-navy-700 hover:text-virgo-gold-200'
                    }`}
                    title={collapsed ? item.label : ''}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </>
        )}

        {/* Team Presence Section - Staff/Admin only */}
        {(isStaff || isAdmin) && (
          <div className="mt-4 border-t border-virgo-teal-900/50">
            <TeamPresenceList collapsed={collapsed} />
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="border-t border-virgo-teal-900 p-2">
        {/* Calendar event banner - shows when in a meeting */}
        {!collapsed && myPresence?.calendar_event?.is_in_meeting && (
          <div className="px-3 py-2 mb-1 bg-slate-900/30 rounded-lg border border-cyan-500/30">
            <div className="flex items-center text-cyan-300 text-xs">
              <span className="w-2 h-2 bg-cyan-500 rounded-full mr-2 animate-pulse" />
              In Meeting
            </div>
            <div className="text-xs text-cyan-200 truncate mt-0.5">
              {myPresence.calendar_event.title}
            </div>
          </div>
        )}

        {/* Status Selector - only show when not collapsed */}
        {!collapsed && myPresence && (
          <div className="px-3 py-2 mb-1">
            <StatusSelector
              currentStatus={myPresence.status || UserStatus?.AVAILABLE || 'available'}
              onSelect={(status) => updateStatus(status)}
              className="w-full"
            />
            {/* Show effective status if different from set status */}
            {myPresence.effective_status && myPresence.effective_status !== myPresence.status && (
              <div className="text-xs text-virgo-teal-500 mt-1 pl-1">
                Showing as: {myPresence.effective_status === 'idle' ? 'Idle' :
                             myPresence.effective_status === 'in_meeting' ? 'In Meeting' :
                             myPresence.effective_status}
              </div>
            )}
          </div>
        )}
        <Link
          to="/profile"
          onClick={(e) => handleNavClick(e, '/profile')}
          className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
            isActive('/profile')
              ? 'bg-gradient-to-r from-virgo-teal-700 to-virgo-teal-600 text-white'
              : 'text-virgo-teal-200 hover:bg-virgo-navy-700'
          }`}
          title={collapsed ? user?.username : ''}
        >
          <div className="relative">
            <User size={20} className="flex-shrink-0" />
            {/* Status dot on avatar when collapsed - use effective_status */}
            {collapsed && myPresence && (
              <StatusDot
                status={myPresence.effective_status || myPresence.status || 'available'}
                size="xs"
                className="absolute -bottom-0.5 -right-0.5 ring-2 ring-virgo-navy-800"
              />
            )}
          </div>
          {!collapsed && <span className="truncate">{user?.username}</span>}
        </Link>
        {/* Presence Settings Button */}
        {!collapsed && (
          <button
            onClick={() => setShowPresenceSettings(true)}
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-virgo-teal-200 hover:bg-virgo-navy-700 hover:text-virgo-teal-100 transition-colors mt-1"
          >
            <Settings size={20} className="flex-shrink-0" />
            <span>Status Settings</span>
          </button>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-virgo-teal-200 hover:bg-virgo-navy-700 hover:text-virgo-gold-300 transition-colors mt-1"
          title={collapsed ? 'Logout' : ''}
        >
          <LogOut size={20} className="flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Presence Settings Modal */}
      <PresenceSettingsModal
        isOpen={showPresenceSettings}
        onClose={() => setShowPresenceSettings(false)}
      />
    </div>
  );
};

export default Sidebar;
